import {
  Card,
  CardContent,
  Fade,
  makeStyles,
  Tab,
  Tabs,
  TextField
} from "@material-ui/core";
import grey from "@material-ui/core/colors/grey";
import CardTitle from "@saleor/components/CardTitle/CardTitle";
import FormSpacer from "@saleor/components/FormSpacer";
import RichTextEditor from "@saleor/components/RichTextEditor/RichTextEditor";
import { FormChange } from "@saleor/hooks/useForm";
import { commonMessages } from "@saleor/intl";
import { getFormErrors } from "@saleor/utils/errors";
import classnames from "classnames";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { CouponError } from "../CouponCreatePage";
import { TooltipArrowLeft, TooltipArrowUp } from "../CouponDetails/Tooltip";

const useStyles = makeStyles(
  {
    cardTitle: {
      "& span": {
        fontWeight: "bold"
      }
    },
    errorCouponTab: {
      color: "#EC5E77 !important"
    },
    errorTabLabel: {
      color: "#EC5E77"
    },
    helperText: {
      "& span": {
        color: grey[800]
      },
      color: "#EC5D77",
      margin: "3px 0px"
    },
    root: {
      textTransform: "none"
    },
    selectedCouponTabItem: {
      color: "#0030FF"
    },
    tabContent: {
      display: "none"
    },
    tabContentActive: {
      display: "block"
    },
    tabRoot: {
      fontSize: "16px",
      textTransform: "none"
    },
    tabsRoot: {
      "& .MuiTabs-flexContainer": {
        borderBottom: "2px solid #CED3DD"
      },
      "& .MuiTabs-indicator": {
        backgroundColor: "#0030FF"
      },
      fontSize: "16px",
      marginBottom: 12,
      textTransform: "none"
    }
  },
  { name: "CouponInformation" }
);
interface CouponInformationProps {
  data: any;
  disabled: boolean;
  errors: CouponError[];
  localError: {
    mainPromoDescription: string;
    mainPromoTitle: string;
    mechanics: string;
    subtitle: string;
    tagline: string;
    accessPageDescription: string;
  };
  initialDescription?: any;
  onChange: FormChange;
}

export const CouponInformation: React.FC<CouponInformationProps> = props => {
  const {
    data,
    disabled,
    errors,
    localError,
    onChange,
    initialDescription
  } = props;

  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(0);
  const formErrors = getFormErrors(
    [
      "mainPromoTitle",
      "mainPromoDescription",
      "mechanics",
      "subtitle",
      "tagline",
      "accessPageDescription"
    ],
    errors || []
  );
  const classes = useStyles(props);
  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  const hasMainPromoTitleError =
    !!formErrors.mainPromoTitle || !!localError.mainPromoTitle;
  const hasContentError =
    !!formErrors.mainPromoDescription || !!localError.mainPromoDescription;
  const hasMechanicsContentError =
    !!formErrors.mechanics || !!localError.mechanics;
  const hasSubtitleError = !!formErrors.subtitle || !!localError.subtitle;
  const hasTaglineError = !!formErrors.tagline || !!localError.tagline;
  const hasShortDescriptionError =
    !!formErrors.accessPageDescription || !!localError.accessPageDescription;

  const tabsClassNames = classnames({
    "error-tabs":
      hasMainPromoTitleError ||
      hasContentError ||
      hasMechanicsContentError ||
      hasSubtitleError ||
      hasTaglineError ||
      hasShortDescriptionError
  });

  const handleFieldChange = (fieldName: string) => (
    event: React.ChangeEvent<any>
  ) => {
    onChange({
      target: {
        name: fieldName,
        value: event.target.value
      }
    });
  };

  const handleMainPromoTitleChange = handleFieldChange("mainPromoTitle");
  const handleSubtitleChange = handleFieldChange("subtitle");
  const handleTaglineChange = handleFieldChange("tagline");

  return (
    <Card>
      <CardTitle
        className={classes.cardTitle}
        title={intl.formatMessage({
          defaultMessage: "Coupon Information",
          description: "Coupon Information "
        })}
      />
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        className={tabsClassNames}
        classes={{ root: classes.tabsRoot }}
        indicatorColor="primary"
      >
        <Tab
          id="mainPromoTab"
          label={intl.formatMessage({ defaultMessage: "Main Promo" })}
          className={
            hasMainPromoTitleError || hasContentError
              ? `${classes.errorTabLabel} ${classes.errorCouponTab}`
              : null
          }
          classes={{
            root: classes.tabRoot,
            selected: classes.selectedCouponTabItem
          }}
        />

        <Tab
          id="mechanicsTab"
          label={intl.formatMessage({ defaultMessage: "Mechanics" })}
          className={
            hasMechanicsContentError
              ? `${classes.errorTabLabel} ${classes.errorCouponTab}`
              : null
          }
          classes={{
            root: classes.tabRoot,
            selected: classes.selectedCouponTabItem
          }}
        />
        <Tab
          id="accessPageTab"
          label={intl.formatMessage({ defaultMessage: "Access Page" })}
          className={
            hasSubtitleError || hasTaglineError || hasShortDescriptionError
              ? `${classes.errorTabLabel} ${classes.errorCouponTab}`
              : null
          }
          classes={{
            root: classes.tabRoot,
            selected: classes.selectedCouponTabItem
          }}
        />
      </Tabs>
      <Fade in={activeTab === 0}>
        <div
          className={
            activeTab === 0 ? classes.tabContentActive : classes.tabContent
          }
        >
          <CardContent>
            <TextField
              id="mainPromoTitleTxt"
              label={intl.formatMessage({
                defaultMessage: "Main Promo Title",
                description: "Main Promo Title"
              })}
              name="mainPromoTitle"
              fullWidth
              inputProps={{
                maxLength: 255
              }}
              error={!!formErrors.mainPromoTitle || !!localError.mainPromoTitle}
              value={data.mainPromoTitle}
              onChange={handleMainPromoTitleChange}
              FormHelperTextProps={{
                className: classes.helperText
              }}
              helperText={
                <>
                  <span>
                    Characters Remaining: {255 - data.mainPromoTitle.length}
                  </span>
                  <br />
                  {localError.mainPromoTitle ? "This field is required." : ""}
                </>
              }
              disabled={disabled}
            />
            <FormSpacer />
            <TooltipArrowLeft
              title={
                data.couponClassification === "DEALS"
                  ? "Should be at least 3 sentences indicating original and discounted price, duration and special product feature"
                  : ""
              }
              placement="right"
            >
              <div>
                <RichTextEditor
                  disabled={disabled}
                  error={
                    !!formErrors.mainPromoDescription ||
                    !!localError.mainPromoDescription
                  }
                  initial={initialDescription?.mainPromoDescription || ""}
                  label={intl.formatMessage(
                    commonMessages.mainPromoDescription
                  )}
                  name="mainPromoDescription"
                  onChange={onChange}
                  helperText={
                    localError.mainPromoDescription
                      ? "This field is required."
                      : ""
                  }
                />
              </div>
            </TooltipArrowLeft>
          </CardContent>
        </div>
      </Fade>
      <Fade in={activeTab === 1}>
        <div
          className={
            activeTab === 1 ? classes.tabContentActive : classes.tabContent
          }
        >
          <CardContent>
            <TooltipArrowLeft
              title={
                data.couponClassification === "DEALS"
                  ? "Should indicate complete promo mechanics, special instructions, terms & conditions."
                  : ""
              }
              placement="right"
            >
              <div>
                <RichTextEditor
                  disabled={disabled}
                  error={!!formErrors.mechanics || !!localError.mechanics}
                  initial={initialDescription?.mechanics || ""}
                  label={intl.formatMessage(commonMessages.mechanics)}
                  name="mechanics"
                  onChange={onChange}
                  helperText={
                    localError.mechanics ? "This field is required." : ""
                  }
                />
              </div>
            </TooltipArrowLeft>
          </CardContent>
        </div>
      </Fade>
      <Fade in={activeTab === 2}>
        <div
          className={
            activeTab === 2 ? classes.tabContentActive : classes.tabContent
          }
        >
          <CardContent>
            <TooltipArrowUp
              title={
                data.couponClassification === "DEALS"
                  ? "Pro-tip: You can input here the complete tenant name or mall name."
                  : "Should indicate the Mall Name together with the word “Events”"
              }
              placement="bottom"
            >
              <div>
                <TextField
                  id="subtitleTxt"
                  label={intl.formatMessage({
                    defaultMessage: "Subtitle",
                    description: "Subtitle"
                  })}
                  name="subtitle"
                  fullWidth
                  error={!!formErrors.subtitle || !!localError.subtitle}
                  value={data.subtitle}
                  onChange={handleSubtitleChange}
                  FormHelperTextProps={{
                    className: classes.helperText
                  }}
                  helperText={
                    <>
                      <span>
                        Characters Remaining: {255 - data.subtitle.length}
                      </span>
                      <br />
                      {localError.subtitle ? "This field is required." : ""}
                    </>
                  }
                  inputProps={{ maxLength: 255 }}
                  disabled={disabled}
                />
              </div>
            </TooltipArrowUp>
            <FormSpacer />
            <TextField
              id="taglineTxt"
              label={intl.formatMessage({
                defaultMessage: "Tagline",
                description: "Tagline"
              })}
              name="tagline"
              fullWidth
              error={!!formErrors.tagline || !!localError.tagline}
              value={data.tagline}
              onChange={handleTaglineChange}
              FormHelperTextProps={{
                className: classes.helperText
              }}
              helperText={
                <>
                  <span>Characters Remaining: {255 - data.tagline.length}</span>
                  <br />
                  {localError.tagline ? "This field is required." : ""}
                </>
              }
              inputProps={{
                maxLength: 255
              }}
              disabled={disabled}
            />
            <FormSpacer />
            <TooltipArrowLeft
              title={
                data.couponClassification === "EVENTS"
                  ? "Should indicate the instruction on how to visit and participate to the event."
                  : ""
              }
              placement="right"
            >
              <div>
                <RichTextEditor
                  disabled={disabled}
                  error={
                    !!formErrors.accessPageDescription ||
                    !!localError.accessPageDescription
                  }
                  helperText={
                    localError.accessPageDescription
                      ? "This field is required."
                      : ""
                  }
                  initial={initialDescription?.accessPageDescription || ""}
                  label={intl.formatMessage(
                    commonMessages.accessPageDescription
                  )}
                  name="accessPageDescription"
                  onChange={onChange}
                />
              </div>
            </TooltipArrowLeft>
          </CardContent>
        </div>
      </Fade>
    </Card>
  );
};

export default CouponInformation;
